/**
 * webauthn-errors/master-messages.ts — master-passkey server error codes
 *
 * Device-limit + master-passkey spec PR B (#1605, module 02 §4/§7). Split out
 * of messages.ts (300-line modularity rule) — mirrors the feature-flags.ts /
 * feature-flags-metadata.ts split precedent. Re-exported into
 * SERVER_ERROR_MESSAGES so `resolveWebAuthnError` needs no separate lookup.
 *
 * Error philosophy (messages.ts, verbatim): never say "error" without telling
 * the user what to do; amber for user-correctable states (never red — red is
 * Forensic Pause / critical OSINT only); no liveness/deepfake claim anywhere —
 * the master pin is possession + freshness (ledger #16 / spec module 03 §5).
 */

import type { WebAuthnUserMessage } from "./types";

export const MASTER_ERROR_MESSAGES: Record<string, WebAuthnUserMessage> = {

  // Lost vs stolen is deliberately copy-distinct here (review fix advai
  // Q-002, security Q1): a 72h transfer only helps when the master is LOST
  // (nobody else holds it, so nothing can veto the hold). If it was STOLEN,
  // whoever holds it can veto that same transfer — guardian recovery is the
  // real counter-move, and this message must not blur the two into one
  // "start a transfer" instruction (copy.ts MASTER_LOST_NOTICE /
  // MASTER_STOLEN_WARNING carry the same distinction elsewhere).
  MASTER_CREDENTIAL_REQUIRED: {
    title:       "Master passkey required",
    description: "This action requires your master passkey. If you have it, confirm with it now — a cross-device prompt will appear if it's registered on another device. If it's lost — nobody else holds it — start a 72-hour transfer to move master status to a passkey you do have. If someone else has it instead, they can veto that transfer — guardian recovery is the way to regain control.",
    retryable:   true,
    severity:    "amber",
  },

  MASTER_GUARDIAN_SET_REQUIRED: {
    title:       "Guardians required first",
    description: "Designating a master passkey requires a recovery-capable guardian set first — guardian recovery is the only escape if your master is ever lost or stolen. Enrol your guardians, then designate a master.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_HARDWARE_CONFLICT: {
    title:       "Master passkey can't complete this action",
    description: "This action requires an attested hardware key, but your master passkey isn't one. Transfer or clear the master with your current master passkey and designate a hardware key, or contact support.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_ANOMALY_BLOCKED: {
    title:       "Paused during a security alert",
    description: "A critical security alert is active on your account — master transfers are paused until it clears.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_ALREADY_DESIGNATED: {
    title:       "A master passkey already exists",
    description: "Transfer or clear your current master passkey instead of designating a new one.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_HOLD_ALREADY_PENDING: {
    title:       "A transfer is already in progress",
    description: "Only one master designation or transfer can be in progress at a time. Resolve it first — check the pending request below.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_NONE_DESIGNATED: {
    title:       "No master passkey designated",
    description: "This action needs an existing master passkey — designate one first.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_NO_ARMING_HOLD: {
    title:       "Nothing to cancel",
    description: "There is no master designation currently arming.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_NO_PENDING_HOLD: {
    title:       "Nothing to veto",
    description: "There is no pending master transfer to veto.",
    retryable:   false,
    severity:    "amber",
  },

  MASTER_CANDIDATE_INVALID: {
    title:       "That passkey isn't available",
    description: "The passkey you chose is not an active credential on your account. Refresh your device list and try again.",
    retryable:   true,
    severity:    "amber",
  },

  // AC-8 (#3099): a passkey held after an incident cannot become master.
  MASTER_CANDIDATE_HELD: {
    title:       "That passkey is on hold",
    description: "That passkey is on hold after a security incident. Choose a passkey that is not on hold.",
    retryable:   true,
    severity:    "amber",
  },

  MASTER_CHALLENGE_ACTION_MISMATCH: {
    title:       "Verification expired",
    description: "This verification was started for a different action. Start again from Security Keys.",
    retryable:   true,
    severity:    "amber",
  },

  MASTER_ASSERTION_MISMATCH: {
    title:       "Wrong passkey used",
    description: "This action must be confirmed with the specific passkey it was started for. Try again and use that exact passkey.",
    retryable:   true,
    severity:    "amber",
  },
};
